import { useEffect, useState } from 'react';
import { IconClose } from '../icons.js';
import { useSheetDrag } from '../../../hooks/useSheetDrag.js';

export interface WebSource {
  title: string;
  url: string;
}

interface SourcesSheetProps {
  sources: ReadonlyArray<WebSource>;
  onClose: () => void;
}

/** Hostname legible (`www.` fuera) — subtítulo de cada fuente. Tolerante a URLs raras. */
function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/**
 * Detalle de las fuentes de una búsqueda web del coach. Bottom sheet (mismo
 * patrón que `DrillModal`: overlay que cierra al tocar afuera + hoja con handle).
 * Se abre desde el chip sutil "🌐 N fuentes" en la conversación — así las citas
 * no ensucian el hilo, pero siguen a un tap de distancia.
 */
export function SourcesSheet({ sources, onClose }: SourcesSheetProps) {
  const { grabProps, sheetStyle } = useSheetDrag(onClose);
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div className="web-sources-overlay" onClick={onClose}>
      <div
        className="web-sources-sheet" style={sheetStyle}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Fuentes de la búsqueda web"
      >
        <div className="drill-handle sheet-grab" {...grabProps} />

        <div className="web-sources-head">
          <div className="web-sources-title">
            <span aria-hidden>🌐</span>
            <span>Fuentes</span>
            <span className="web-sources-count">{sources.length}</span>
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            className="web-sources-close"
            type="button"
          >
            <IconClose size={14} />
          </button>
        </div>

        <div className="web-sources-list">
          {sources.map((s, i) => (
            <a
              key={`${s.url}-${i}`}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="web-sources-row"
            >
              <span className="web-sources-row-title">{s.title || hostOf(s.url)}</span>
              <span className="web-sources-row-host">{hostOf(s.url)}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Chip sutil "🌐 N fuentes" que se ancla bajo la burbuja del coach que hizo la
 * búsqueda. Cada burbuja con `sources` renderiza su propio chip (con su propio
 * estado de apertura), así el detalle scrollea con su mensaje y una búsqueda
 * nueva genera otro chip bajo su propia burbuja.
 */
export function BotSourcesChip({ sources }: { sources: ReadonlyArray<WebSource> }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="web-sources-anchor">
      <button
        type="button"
        className="web-sources-chip"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
      >
        <span aria-hidden>🌐</span>
        <span>
          {sources.length} {sources.length === 1 ? 'fuente' : 'fuentes'}
        </span>
        <span className="web-sources-chip-caret" aria-hidden>
          ›
        </span>
      </button>
      {open && <SourcesSheet sources={sources} onClose={() => setOpen(false)} />}
    </div>
  );
}
